// Refresh do access token. Refresh token vive em cookie httpOnly (readme §16),
// portanto a chamada NÃO envia body — só `credentials: 'include'`.
//
// ## Por que o retorno é tipado e não `string | null`
//
// Antes qualquer falha virava `null`, e quem chamava reagia apagando a sessão. Um 502
// durante um deploy, um 504 do nginx ou uma queda de Wi-Fi eram indistinguíveis de
// "seu refresh token é inválido" — e expulsavam o usuário, que precisava logar de
// novo mesmo com o cookie intacto. Agora o motivo vem junto:
//
//   - `auth`      — o servidor recusou de forma definitiva (401/403). A sessão morreu
//                   mesmo; pode limpar tudo.
//   - `transient` — rede, timeout, 5xx, 429. O token não foi renovado, mas a sessão
//                   continua válida. NÃO desloga: a próxima tentativa deve funcionar.
import { setToken } from './token-storage';

interface RefreshResponse {
  accessToken: string;
}

export type RefreshFailureReason = 'auth' | 'transient';

export type RefreshResult =
  | { ok: true; token: string }
  | { ok: false; reason: RefreshFailureReason };

/// Nome da trava entre abas (Web Locks API).
const LOCK_NAME = 'chatia:auth-refresh';

let inflight: Promise<RefreshResult> | null = null;

/**
 * Um 401 do próprio `/auth/refresh` significa que o cookie não vale mais — isso é
 * definitivo. Um 5xx/429 é o servidor com problema, não a credencial.
 */
function classifyStatus(status: number): RefreshFailureReason {
  return status === 401 || status === 403 ? 'auth' : 'transient';
}

async function requestRefresh(): Promise<RefreshResult> {
  try {
    // Usa Request construído explicitamente — em testes (Vitest + undici) o
    // global Request é patched pra resolver URLs relativas; em produção é
    // o mesmo comportamento do browser nativo.
    const req = new Request('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const res = await fetch(req);
    if (!res.ok) return { ok: false, reason: classifyStatus(res.status) };
    const data = (await res.json()) as RefreshResponse;
    if (!data?.accessToken) return { ok: false, reason: 'transient' };
    setToken(data.accessToken);
    return { ok: true, token: data.accessToken };
  } catch {
    // Exceção do fetch = rede/abort. Nunca é veredito sobre a credencial.
    return { ok: false, reason: 'transient' };
  }
}

/**
 * Renova o access token. Deduplica dentro da aba (`inflight`) e ENTRE abas
 * (Web Locks), quando o browser suporta.
 *
 * A trava entre abas importa porque o servidor rotaciona o refresh token e trata a
 * reapresentação de um token já rotacionado como comprometimento: ele revoga a cadeia
 * inteira do usuário. Duas abas renovando ao mesmo tempo com o mesmo cookie derrubam
 * TODAS as sessões daquele usuário de uma vez. Serializando, a segunda aba entra na
 * trava depois que o cookie já foi atualizado e apresenta o token novo.
 */
export async function refreshAccessToken(): Promise<RefreshResult> {
  // Dedup: se um refresh já está acontecendo, todas as requisições aguardam o mesmo.
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      if (!locks) return await requestRefresh();
      return await locks.request(LOCK_NAME, () => requestRefresh());
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}
